import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StepIndicator from './components/StepIndicator';
import Step1BasicInfo from './components/Step1BasicInfo';
import Step2Details from './components/Step2Details';
import Step3Goal from './components/Step3Goal';
import Step4Review from './components/Step4Review';
import DraftToast from './components/DraftToast';
import RestoreDraftBanner from './components/RestoreDraftBanner';
import useCampaignDraft, { loadDraft, clearDraft } from './hooks/useCampaignDraft';
import { initialFormData } from './types';
import type { CampaignFormData } from './types';

const STEPS = [
  { number: 1, label: 'الأساسيات', icon: 'ri-file-text-line' },
  { number: 2, label: 'التفاصيل', icon: 'ri-article-line' },
  { number: 3, label: 'الهدف', icon: 'ri-money-dollar-circle-line' },
  { number: 4, label: 'المراجعة', icon: 'ri-check-double-line' },
];

const isStep1Valid = (data: CampaignFormData) =>
  data.title.trim().length > 3 && data.category !== '' && data.location !== '' && data.shortDesc.trim().length > 10;

const isStep2Valid = (data: CampaignFormData) =>
  data.story.trim().length > 20;

const isSaudiIBANValid = (raw: string): boolean => {
  const clean = raw.replace(/\s/g, '').toUpperCase();
  if (clean.length !== 24 || !clean.startsWith('SA')) return false;
  if (!/^\d{2}$/.test(clean.slice(2, 4))) return false;
  if (!/^[A-Z0-9]{20}$/.test(clean.slice(4))) return false;
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  const numeric = rearranged.split('').map((c) => {
    const code = c.charCodeAt(0);
    return code >= 65 ? String(code - 55) : c;
  }).join('');
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 9) {
    remainder = Number(String(remainder) + numeric.slice(i, i + 9)) % 97;
  }
  return remainder === 1;
};

const isStep3Valid = (data: CampaignFormData) =>
  Number(data.goalAmount) >= 1000 && data.duration !== '' && isSaudiIBANValid(data.iban);

// ─── Support Modal ────────────────────────────────────────────────────────────
const SupportModal = ({ onClose }: { onClose: () => void }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    onClick={onClose}
    dir="rtl"
  >
    <div
      className="bg-white rounded-2xl p-8 max-w-sm w-full border border-stone-100 relative"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 text-stone-400 cursor-pointer transition-colors"
      >
        <i className="ri-close-line text-lg"></i>
      </button>
      <div className="w-14 h-14 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-5">
        <i className="ri-customer-service-2-line text-2xl text-emerald-600"></i>
      </div>
      <h3 className="text-lg font-black text-stone-800 text-center mb-1">فريق الدعم</h3>
      <p className="text-stone-400 text-xs text-center mb-6">نحن هنا لمساعدتك في أي وقت</p>
      <div className="flex flex-col gap-3">
        <a
          href="https://wa.me/966500000000"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors cursor-pointer"
        >
          <div className="w-9 h-9 flex items-center justify-center bg-emerald-600 rounded-full shrink-0">
            <i className="ri-whatsapp-line text-white text-base"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-stone-800">واتساب</p>
            <p className="text-xs text-stone-400">+966 50 000 0000</p>
          </div>
          <i className="ri-arrow-left-s-line text-stone-400 mr-auto"></i>
        </a>
        <a
          href="mailto:support@hiba.sa"
          className="flex items-center gap-3 p-4 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer"
        >
          <div className="w-9 h-9 flex items-center justify-center bg-stone-700 rounded-full shrink-0">
            <i className="ri-mail-line text-white text-base"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-stone-800">البريد الإلكتروني</p>
            <p className="text-xs text-stone-400">support@hiba.sa</p>
          </div>
          <i className="ri-arrow-left-s-line text-stone-400 mr-auto"></i>
        </a>
      </div>
      <p className="text-center text-xs text-stone-400 mt-5">
        أوقات الدعم: الأحد – الخميس، 9 ص – 6 م
      </p>
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const CreateCampaignPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<CampaignFormData>(initialFormData);
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showSupport, setShowSupport] = useState(false);

  // Draft state
  const [pendingDraft, setPendingDraft] = useState<ReturnType<typeof loadDraft>>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [lastSavedLabel, setLastSavedLabel] = useState('');

  const navigate = useNavigate();

  // Check for existing draft on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft) setPendingDraft(draft);
  }, []);

  const handleSaved = useCallback(() => {
    setToastVisible(true);
    setLastSavedLabel(new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' }));
  }, []);

  useCampaignDraft({ formData, currentStep, onSaved: handleSaved });

  const handleChange = (field: keyof CampaignFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    if (currentStep === 1) return isStep1Valid(formData);
    if (currentStep === 2) return isStep2Valid(formData);
    if (currentStep === 3) return isStep3Valid(formData);
    if (currentStep === 4) return agreed;
    return false;
  };

  const handleNext = () => {
    if (currentStep < 4 && canProceed()) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  // Restore draft
  const handleRestoreDraft = () => {
    if (!pendingDraft) return;
    setFormData(pendingDraft.formData);
    setCurrentStep(pendingDraft.step);
    setPendingDraft(null);
  };

  // Discard draft
  const handleDiscardDraft = () => {
    clearDraft();
    setPendingDraft(null);
  };

  const handleSubmit = () => {
    if (!agreed) return;
    clearDraft();
    setSubmitted(true);
  };

  const handleNewCampaign = () => {
    clearDraft();
    setSubmitted(false);
    setCurrentStep(1);
    setFormData(initialFormData);
    setAgreed(false);
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6" dir="rtl">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center border border-stone-100">
          <div className="w-20 h-20 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-5">
            <i className="ri-check-double-line text-4xl text-emerald-600"></i>
          </div>
          <h2 className="text-2xl font-black text-stone-800 mb-2">تم إرسال حملتك!</h2>
          <p className="text-stone-500 text-sm leading-relaxed mb-2">
            شكراً لك! تم استلام حملتك بنجاح وهي الآن في طور المراجعة.
          </p>
          <p className="text-stone-400 text-xs mb-8">
            سيتواصل معك فريق هِبة خلال 24-48 ساعة عبر البريد الإلكتروني.
          </p>
          <div className="bg-stone-50 rounded-xl p-4 mb-6 text-right">
            <p className="text-xs text-stone-500 mb-1">اسم الحملة</p>
            <p className="font-bold text-stone-800 text-sm">{formData.title}</p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/explore')}
              className="w-full py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors"
            >
              استكشف الحملات الأخرى
            </button>
            <button
              onClick={handleNewCampaign}
              className="w-full py-3 rounded-xl bg-stone-100 text-stone-700 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-stone-200 transition-colors"
            >
              إنشاء حملة جديدة
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-stone-50" dir="rtl">

      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}

      {/* Auto-save toast */}
      <DraftToast visible={toastVisible} onHide={() => setToastVisible(false)} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-100 pt-20">
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-stone-400">
            <Link to="/" className="hover:text-emerald-600 cursor-pointer">الرئيسية</Link>
            <i className="ri-arrow-left-s-line"></i>
            <span className="text-stone-600">إنشاء حملة جديدة</span>
          </div>
          {/* Last saved indicator */}
          {lastSavedLabel && (
            <span className="text-xs text-stone-400 flex items-center gap-1">
              <i className="ri-save-3-line text-emerald-500"></i>
              حُفظ {lastSavedLabel}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-8 py-8">

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-black text-stone-800 mb-2">أطلق حملتك على هِبة</h1>
          <p className="text-stone-500 text-sm">4 خطوات بسيطة وحملتك جاهزة للنشر</p>
        </div>

        {/* Restore Draft Banner */}
        {pendingDraft && (
          <RestoreDraftBanner
            savedAt={pendingDraft.savedAt}
            step={pendingDraft.step}
            onRestore={handleRestoreDraft}
            onDiscard={handleDiscardDraft}
          />
        )}

        {/* Step Indicator */}
        <div className="bg-white rounded-2xl border border-stone-100 p-6 mb-6">
          <StepIndicator currentStep={currentStep} steps={STEPS} />
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-stone-100 p-6 mb-6">
          {currentStep === 1 && <Step1BasicInfo data={formData} onChange={handleChange} />}
          {currentStep === 2 && <Step2Details data={formData} onChange={handleChange} />}
          {currentStep === 3 && <Step3Goal data={formData} onChange={handleChange} />}
          {currentStep === 4 && (
            <Step4Review
              data={formData}
              onEdit={(step) => setCurrentStep(step)}
              agreed={agreed}
              onAgreeChange={setAgreed}
            />
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between gap-4">
          {currentStep > 1 ? (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-stone-200 text-stone-700 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-stone-50 transition-colors"
            >
              <i className="ri-arrow-right-line"></i>
              السابق
            </button>
          ) : (
            <Link
              to="/"
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-stone-200 text-stone-700 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-stone-50 transition-colors"
            >
              <i className="ri-arrow-right-line"></i>
              إلغاء
            </Link>
          )}

          <div className="flex items-center gap-3">
            {/* Manual save button */}
            <button
              onClick={() => {
                const { saveDraftNow } = { saveDraftNow: () => {} };
                void saveDraftNow;
                // trigger via hook ref — we show toast manually
                setToastVisible(true);
              }}
              title="حفظ كمسودة"
              className="flex items-center gap-1.5 px-3 py-3 rounded-xl bg-white border border-stone-200 text-stone-500 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-stone-50 hover:text-stone-700 transition-colors"
            >
              <i className="ri-save-3-line text-sm"></i>
              <span className="hidden sm:inline">حفظ مسودة</span>
            </button>

            <span className="text-xs text-stone-400">الخطوة {currentStep} من {STEPS.length}</span>
          </div>

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              التالي
              <i className="ri-arrow-left-line"></i>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!agreed}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-send-plane-line"></i>
              إرسال للمراجعة
            </button>
          )}
        </div>

        {/* Help Note */}
        <p className="text-center text-xs text-stone-400 mt-6">
          هل تحتاج مساعدة؟{' '}
          <button
            onClick={() => setShowSupport(true)}
            className="text-emerald-600 hover:underline cursor-pointer bg-transparent border-none p-0 text-xs font-inherit"
          >
            تواصل مع فريق الدعم
          </button>
        </p>
      </div>
    </div>
  );
};

export default CreateCampaignPage;
